import type { AnalyticsPeriodParams, SummaryMetric } from '../../../shared/api/analytics.api'

const RU_MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
]

const RU_MONTHS_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
]

/** Целые ₽ с разделителями: «1 234 567 ₽». */
export function fmtRub(n: number): string {
  return `${Math.round(n).toLocaleString('ru-RU')} ₽`
}

/** Короткие ₽ для осей/бейджей: «62,6 млн ₽». */
export function fmtRubShort(n: number): string {
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.', ',')} млн ₽`
  if (abs >= 1_000) return `${Math.round(n / 1_000)} тыс ₽`
  return `${Math.round(n)} ₽`
}

export function fmtInt(n: number): string {
  return n.toLocaleString('ru-RU')
}

/**
 * Границы периода датами YYYY-MM-DD (включительно) — для ссылок с KPI-карточек
 * в список заявок (`/requests?createdFrom=…&createdTo=…`). Календарная логика
 * 1:1 с parsePlanningPeriod на бэке (UTC): month/year — календарные, quarter —
 * 3 месяца начиная с месяца date, custom — as-is.
 */
export function periodDateRange(p: AnalyticsPeriodParams): { from: string; to: string } | null {
  if (p.period === 'custom') {
    return p.from && p.to ? { from: p.from, to: p.to } : null
  }
  const now = new Date()
  const iso = (y: number, m: number, d: number) =>
    `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  if (p.period === 'year') {
    const y = parseInt((p.date ?? String(now.getUTCFullYear())).slice(0, 4), 10)
    if (!Number.isFinite(y)) return null
    return { from: iso(y, 1, 1), to: iso(y, 12, 31) }
  }
  // month / quarter — от месяца date (дефолт — текущий, как на бэке).
  const ym = p.date ?? `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
  const y = parseInt(ym.slice(0, 4), 10)
  const m = parseInt(ym.slice(5, 7), 10)
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null
  const span = p.period === 'quarter' ? 3 : 1
  // День 0 следующего за окном месяца (UTC) = последний день окна.
  const end = new Date(Date.UTC(y, m - 1 + span, 0))
  return {
    from: iso(y, m, 1),
    to: iso(end.getUTCFullYear(), end.getUTCMonth() + 1, end.getUTCDate()),
  }
}

/** Человекочитаемая метка периода из параметров селектора. */
export function periodLabel(p: AnalyticsPeriodParams): string {
  if (p.period === 'custom' && p.from) {
    const fmt = (iso: string) => {
      const [, m, d] = iso.split('-').map(Number)
      return `${d} ${RU_MONTHS_SHORT[(m ?? 1) - 1]}`
    }
    return `${fmt(p.from)} – ${fmt(p.to ?? p.from)}`
  }
  if (p.period === 'year') return p.date ?? ''
  if (p.period === 'quarter' && p.date) {
    const y = p.date.slice(0, 4)
    const m = parseInt(p.date.slice(5, 7), 10)
    return `Q${Math.floor((m - 1) / 3) + 1} ${y}`
  }
  // month
  if (p.date) {
    const y = p.date.slice(0, 4)
    const m = parseInt(p.date.slice(5, 7), 10)
    return `${RU_MONTHS[m - 1]} ${y}`
  }
  return ''
}

/** Бейдж дельты «↑12% vs предыдущий». previous=null → ничего. */
export function DeltaBadge({
  metric,
  suffix = 'vs пред.',
  invert = false,
}: {
  metric: SummaryMetric
  suffix?: string
  invert?: boolean
}) {
  if (metric.previous === null) return null
  if (metric.deltaPct === null) {
    // previous=0 при current>0 — рост «с нуля».
    if (metric.current > 0) {
      return <span className="text-[11px] font-medium text-emerald-600">▲ новое</span>
    }
    return <span className="text-[11px] text-gray-400">— {suffix}</span>
  }
  const d = metric.deltaPct
  const positive = invert ? d < 0 : d > 0
  const negative = invert ? d > 0 : d < 0
  const color = positive ? 'text-emerald-600' : negative ? 'text-red-600' : 'text-gray-400'
  const arrow = d > 0 ? '↑' : d < 0 ? '↓' : ''
  return (
    <span className={`text-[11px] font-medium ${color}`}>
      {arrow} {Math.abs(d)}% {suffix}
    </span>
  )
}

/** Цвет конверсии: высокая — зелёная, низкая — красная (как в ClientsTab). */
export function convClass(p: number): string {
  return p >= 30 ? 'text-emerald-600' : p >= 10 ? 'text-amber-600' : 'text-red-600'
}

/**
 * Денежная конверсия «В заказ / Сумма КП», % (доля денег КП, ушедших в заказ).
 * null — когда КП = 0 (сравнивать не с чем). Может превышать 100%, если заказ
 * дороже последнего КП. В отличие от «Конв. по заявкам» (доля заявок в заказ по
 * штукам) — это денежная метрика, чтобы «817 ₽ при КП 13.4 млн» не читалось как
 * 4.2% (та цифра — доля заявок, а не денег).
 */
export function moneyConvPct(ordered: number, kp: number): number | null {
  return kp > 0 ? Math.round((ordered / kp) * 1000) / 10 : null
}
